<template>
    <div 
        tabindex="0" 
        class="mt-4 outline-none" 
        @focus="() => {
            if (!disable) {
                showOptions = true
            }
        }"
    >
        <div v-if="loading" class="row-loading"></div>
        <div v-else>
            <div class="label text-gray-600 text-base"
                :class="{'text-red-500': error}"><span class="text-red-500" v-if="required">*</span> {{ placeholder }}</div>
            <div class="mb-2 border-b"
                :class="{'border-gray-400': !error, 'border-error': error}">
                <div v-if="!hasValue || selected === null || selected === '' || (multiple && selected.length === 0)"
                    @click="showOptions = true"
                    class="text-gray-500 pt-2 pb-3 cursor-pointer">Choose your option</div>
                <div v-else>
                    <div v-if="multiple">
                        <div class="text-gray-800 pt-2 pb-3 cursor-pointer w-full"
                            @click="showOptions = true">
                            <div v-for="(item, index) in selected"
                                class="inline-flex items-center bg-gray-300 shadow py-1 px-3 mr-2 rounded-full mb-2">
                                <div class="text-sm">{{ item[labelKey] || item }}</div>
                                <cancel-icon class="text-gray-500 hover:text-gray-700 ml-2 flex-no-shrink"
                                    width="18"
                                    height="18"
                                    v-if="!disable"
                                    @click.native.stop="selected.splice(index, 1)" />
                            </div>
                        </div>
                    </div>
                    <div v-else
                        class="flex items-center justify-between">
                        <div 
                            class="pt-2 pb-3 w-full"
                            :class="{'text-gray-500': disable, 'text-gray-800 cursor-pointer': !disable}"
                            @click="() => {
                                if (!disable) {
                                    showOptions = true
                                }
                            }"
                        >{{ selected[labelKey] || value }}</div>
                        <div class="ml-3 flex items-center pt-2 pb-3 cursor-pointer"
                            @click="selected = null">
                            <cancel-icon 
                                width="20" 
                                height="20" 
                                class="text-gray-500 hover:text-gray-700"
                                v-if="!disable"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="text-red-500 text-sm"
                v-if="error"
                v-html="error"></div>
            <div class="mt-1 text-sm text-gray-600"
                v-if="helpText"
                v-html="helpText"></div>
            <app-modal 
                :show.sync="showOptions"
                width="md:w-1/3 lg:w-1/4"
                :hasFooter="multiple"
            >
                <template slot="header">Select {{ placeholder }}</template>
                <template slot="body">
                    <app-search v-model="searchQuery"
                        class="mb-3"
                        focus
                        custom-class="border border-transparent bg-gray-300 rounded focus:bg-white focus:border-gray-400 focus:border"></app-search>
                    <div class="overflow-auto"
                        style="max-height: 16rem">
                        <div>
                            <div v-for="option in localOptions">
                                <checkbox class="p-3 hover:bg-gray-300"
                                    v-model="selected"
                                    :alt-value="option"
                                    v-if="multiple">{{ option[labelKey] || option }}</checkbox>
                                <radio class="p-3 hover:bg-gray-300"
                                    v-model="selected"
                                    :alt-value="option"
                                    v-else>{{ option[labelKey] || option }}</radio>
                            </div>
                        </div>
                    </div>
                </template>
                <template slot="footer">
                    <app-button class="btn-blue"
                        @click="handleDone">Done</app-button>
                </template>
            </app-modal>
        </div>
    </div>
</template>
<script>
    import isEmpty from 'lodash/isEmpty';
    import find from 'lodash/find';
    import filter from 'lodash/filter';
    import map from 'lodash/map';

    export default {        
        props: {
            value: null,
            options: {
                type: Array|Object,
                required: true
            },
            multiple: {
                type: Boolean,
                default: false
            },
            placeholder: {
                type: String,
                required: true
            },
            helpText: {
                type: String,
                default: null
            },
            error: {
                type: String,
                default: null
            },
            valueKey: {
                type: String,
                default: null
            },
            labelKey: {
                type: String,
                default: 'label'
            },
            loading: {
                type: Boolean,
                default: false
            },
            required: {
                type: Boolean,
                default: false
            },
            disable: {
                type: Boolean,
                default: false,
            }
        },
        data() {
            return {
                showOptions: false,
                selected: this.multiple ? [] : {},
                searchQuery: null,
                localOptions: []
            }
        },
        computed: {
            hasValue() {
                if (typeof this.value === 'object') {
                    return !isEmpty(this.value);
                }

                return this.value !== null && this.value !== '';
            }
        },
        watch: {
            showOptions(show) {
                if (show) {
                    this.searchQuery = null;
                }
            },
            options(options, old) {
                this.init();
            },
            selected(value) {
                this.$emit('updated', value);

                if (!this.multiple && !isEmpty(this.selected)) {
                    this.handleDone()
                }

                if (!isEmpty(value) && this.multiple && this.valueKey) {
                    this.$emit('input', map(value, (item) => {
                        return item[this.valueKey];
                    }));

                    return;
                }

                if (!isEmpty(value) && typeof value === 'object' && !this.multiple && this.valueKey) {
                    this.$emit('input', value[this.valueKey])
                    return;
                }

                this.$emit('input', value);                
            },
            searchQuery(value) {
                this.localOptions = filter(this.options, (option) => {
                    if (typeof option === 'string' && option) {
                        return option.toLowerCase().includes(value.toLowerCase());
                    }

                    return option[this.labelKey].toLowerCase().includes(value.toLowerCase())
                })
            }
        },
        mounted() {
            setTimeout(() => {
                this.init();
            }, 100);
        },
        methods: {
            init() {     
                this.localOptions = this.options;
                this.selected = this.value;

                if (this.valueKey && typeof this.value !== 'undefined' && !this.multiple) {
                    this.selected = find(this.options, (item) => {
                        return item[this.valueKey] == this.value
                    }) || this.value
                }

                if (this.valueKey && this.multiple && this.value.length) {
                    this.selected = this.value.map(i => {
                        return find(this.options, j => {
                            if (Object.prototype.toString.call(i) === '[object Object]') {
                                return j[this.valueKey] == i[this.valueKey];
                            }

                            return j[this.valueKey] == i;
                        })
                    })
                }
            },
            handleDone() {
                this.showOptions = false
            }
        }
    }
</script>

<style lang="scss" scoped>
    .label {
        -webkit-transform: translateY(0) scale(0.8);
        transform: translateY(2px) scale(0.8);
        -webkit-transform-origin: 0 0;
        transform-origin: 0 0;
    }
</style>